import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import _trim from 'lodash/trim';
import { Value } from 'baseui/select';
import { AppContext, UserContext } from 'commons-ui';

import usePermission from 'routes/usePermission';
import { GAME_ADMIN_RESOURCES, GAME_ADMIN_SCOPES, PAGE_NAMES } from 'constant';

import { GAME_KEY_MAP } from '../../utils/helper';
import useValuesPicker from '../../utils/useValuesPicker';
import useService from '../../utils/useService';

const useCreate = () => {
	const { app, showAlert } = useContext(AppContext);
	const { user } = useContext(UserContext);
	const { hasPermission } = usePermission();
	const { createZkKey } = useService();

	const { getActualValue, PICKER_VALUES } = useValuesPicker();

	const { appName } = app;
	const hasEditPermission = hasPermission({
		entity: GAME_ADMIN_RESOURCES.zookeeper,
		action: GAME_ADMIN_SCOPES[GAME_ADMIN_RESOURCES.zookeeper]?.listKeys,
		appName,
		configPageName: PAGE_NAMES.gameAdmin
	});

	const history = useHistory();
	const location = useLocation();

	const [gameKey, setgameKey] = useState('');
	const [keyName, setKeyName] = useState('');
	const [keyValue, setKeyValue] = useState('');
	const [pickerValue, setPickerValue] = useState<Value>([]);
	const [confirmationModal, setConfirmationModal] = useState<{
		show: boolean;
		message?: string;
		header?: string;
		handleAlertConfirmation?: () => void;
		handleAlertCancel?: () => void;
	}>({ show: false });
	const isUpdateInitiated = useRef(false);

	useEffect(() => {
		setgameKey(GAME_KEY_MAP[appName]);
	}, []);

	const handleCreatekey = async () => {
		if (pickerValue.length === 0) {
			showAlert('Please Select Key Type', { type: 'error' });
			return;
		}
		if (keyName.length === 0) {
			showAlert('Key Name is missing', { type: 'error' });
			return;
		}

		const { error, actualKeyValue } = getActualValue(pickerValue[0].keyType, keyValue);

		if (error) {
			return;
		}

		setConfirmationModal({
			show: true,
			header: `Creating key : ${keyName}`,
			message: `Do you wish to create key with name: ${keyName}, type: ${pickerValue[0].keyType} and value equals to ${keyValue}`,
			handleAlertCancel: () => {
				if (isUpdateInitiated.current) {
					return;
				}
				showAlert('Failed to create key', { type: 'error' });
				setConfirmationModal({ show: false });
			},
			handleAlertConfirmation: async () => {
				const { serviceName } = _get(location, 'state.keysData', {});
				isUpdateInitiated.current = true;
				const res = (
					await createZkKey({
						keyName: _trim(keyName),
						keyValue: actualKeyValue,
						game: gameKey,
						serviceName,
						agent: _get(user, 'userName', ''),
						desc: 'Creating Key'
					})
				).data;

				if (res.success) {
					showAlert('Key Creation Submitted For Review', {
						type: 'success',
						duration: 'INFINITE',
						actionMessage: 'OK',
						actionOnClick: () => {
							history.goBack();
						}
					});
				} else {
					showAlert(`Failed to create Key ${res.message || ''}`, {
						type: 'error',
						actionMessage: 'OK',
						actionOnClick: () => {
							history.goBack();
						}
					});
				}
			}
		});
	};
	const handleKeyNameEnter = (val: string) => {
		setKeyName(val);
	};
	const handleKeyValueEnter = (val: string) => {
		setKeyValue(val);
	};
	const clearValue = () => {
		setKeyValue('');
	};

	const clearKey = () => {
		setKeyName('');
	};

	const handleCancelPress = () => {
		history.goBack();
	};

	return {
		confirmationModal,
		PICKER_VALUES,
		appName,
		keyValue,
		keyName,
		pickerValue,
		hasEditPermission,
		handleCreatekey,
		clearValue,
		clearKey,
		handleKeyNameEnter,
		handleKeyValueEnter,
		setPickerValue,
		handleCancelPress
	};
};

export default useCreate;
